import { useEffect, useId, useRef } from 'react';

import { scheduleHistoryBack, scheduleHistoryPush } from './helpers';

type HistoryState = {
    backHandler?: string[];
};

function getState() {
    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return window.history.state as HistoryState | null;
}

function setState(state: HistoryState, title: string) {
    window.history.pushState(state, `BackHandler-${title}`);
}

function addKeyToState(key: string) {
    const state = getState();
    const keys = (state && state.backHandler) || [];

    setState(
        {
            ...state,
            backHandler: [...keys, key],
        },
        `add-${key}`,
    );
}

function appendKey(key: string) {
    return scheduleHistoryPush({
        key: `useBackButton-${key}`,
        action: () => {
            addKeyToState(key);
        },
        validator: () => {
            if (hasKey(key)) {
                // Пытаемся добавить ключ, который уже есть в history.state. Обычно это означает, что был вызван
                // history.pushState() при активном хуке useBackButton(). Такой сценарий работы не поддерживается.
                console.warn('[Warning] useBackButton: Attempt to add key to window.history, but it already exists.');

                return false;
            }

            return true;
        },
    });
}

function removeKey(key: string) {
    return scheduleHistoryBack({
        key: `useBackButton-${key}`,
        validator: () => {
            if (hasKey(key)) {
                return true;
            }

            // Пытаемся удалить ключ, но его уже нет в history.state. Обычно это означает, что был вызван
            // history.pushState() при активном хуке useBackButton(). Такой сценарий работы не поддерживается.
            console.warn(
                '[Warning] useBackButton: Attempt to remove key from window.history, but it does not exist. Do not use history.pushState() with active useBackButton hook',
            );
            return false;
        },
    });
}

function hasKey(key: string) {
    const state = getState();
    return Boolean(state && state.backHandler && state.backHandler.includes(key));
}

type UseBackButton = () => void;
type HookOptions = {
    enabled: boolean;
};

/**
 * Хук, позволяющий подписаться на нажатие системной кнопки «Back» на Android, а также кнопки «Назад» в браузере.
 * @param callback фукнция, вызываемая при нажатии на Back
 * @param enabled включена ли обработка нажатий
 */
export function useBackButton(callback: UseBackButton, { enabled }: HookOptions = { enabled: true }) {
    const key = useId();

    // Флаг говорит о том, что уникальный ключ добавлен в историю и можно начинать перехватывать события
    const initializedRef = useRef(false);

    // Сохраняем обработчик в ссылку, чтобы не пересоздавать EventListener на каждый рендер
    const callbackRef = useRef(callback);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    callbackRef.current = callback;

    useEffect(() => {
        if (!enabled) {
            return;
        }
        // eslint-disable-next-line @adv-frontend/config/no-floating-promises -- https://st.yandex-team.ru/DIRECT-223857
        appendKey(key).then(() => {
            initializedRef.current = true;
        });

        return () => {
            // eslint-disable-next-line @adv-frontend/config/no-floating-promises -- should be fixed
            removeKey(key).then(() => {
                initializedRef.current = false;
            });
        };
    }, [enabled, key]);

    useEffect(() => {
        const fn = () => {
            // Если ключ еще не был добавлен в историю, значит пока не готовы обрабатывать popstate
            if (!initializedRef.current) {
                return;
            }

            // Если ключ остался в истории, значит сработал другой инстанс хука useBackButton
            if (hasKey(key)) {
                return;
            }

            // Добавляем ключ обратно, чтобы снова ловить popstate для него
            // eslint-disable-next-line @adv-frontend/config/no-floating-promises -- https://st.yandex-team.ru/DIRECT-223857
            appendKey(key).then(() => {
                // Не дожидаемся возвращения ключа в History State,
                // потому что обработчик кнопки назад с большой вероятностью
                // может быть отключен, например при закрытии модального окна.
                // В этом случае не хотим делать pushState() + сразу goBack()
            });

            // Даем обработчику понять, что была нажата кнопка «Назад»
            callbackRef.current();
        };

        window.addEventListener('popstate', fn);

        return () => window.removeEventListener('popstate', fn);
    }, [key, initializedRef, callbackRef]);
}
